import { todayLocal } from '@/lib/dates'
import { useState } from 'react'
import { BookOpen, Plus } from 'lucide-react'
import { EmptyState, ErrorState, FormDialog, PageHeader } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { usePayElements } from '@/features/payroll'
import { toApiProblem } from '@/lib/api/client'
import { useCompensationMutations, useControlAccounts, useGlMappings } from '../api'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

interface LegalEntityOption { id: string; name: string }

/**
 * Which accounts payroll posts to.
 *
 * <p>Part 1 posted with the single account code an element carries and reported the net-pay
 * leg as unmapped rather than inventing one. This is where both stop being unmapped — and
 * still per entity, because a chart of accounts belongs to a company's ledger and there is no
 * sensible default for one.
 */
export function ChartOfAccountsPage() {
  useBreadcrumb()
  const mappings = useGlMappings()
  const controls = useControlAccounts()
  const elements = usePayElements()
  const entities = useLookup<LegalEntityOption>('legalEntities')
  const { saveMapping, saveControlAccount } = useCompensationMutations()

  const [draft, setDraft] = useState<{
    elementId: string; legalEntityId: string; effectiveFrom: string
    debitAccount: string; creditAccount: string; costCentreSource: string
  } | null>(null)
  const [controlDraft, setControlDraft] = useState<{
    legalEntityId: string; netPayAccount: string; roundingAccount: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Chart of accounts"
        subtitle="The debit and credit each pay element posts to, and where net pay is owed."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                elementId: '', legalEntityId: '',
                effectiveFrom: todayLocal(),
                debitAccount: '', creditAccount: '', costCentreSource: 'ASSIGNMENT',
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            Map an element
          </Button>
        }
      />

      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}

      <Card className="p-4">
        <h2 className="mb-1 flex items-center gap-2 text-base font-semibold">
          <BookOpen className="size-4" aria-hidden="true" /> Net-pay control account
        </h2>
        <p className="mb-3 text-sm text-slate-600">
          Where the balancing credit goes — what the company owes its employees until the money
          leaves. Until one is set, that leg posts as <code>UNMAPPED:NET_PAY</code> and the
          journals tab reports it rather than defaulting to an account nobody has.
        </p>
        {controls.isSuccess && controls.data.length === 0 && (
          <p className="mb-3 text-sm text-amber-800">
            No control account is set, so every payroll journal has an unmapped line.
          </p>
        )}
        {controls.data?.map((account) => (
          <p key={account.id} className="text-sm">
            <code>{account.netPayAccount}</code>
            <span className="ms-2 text-slate-600">
              {account.legalEntityId ? 'for one entity' : 'for every entity'}
            </span>
          </p>
        ))}
        <Button
          type="button" variant="outline" size="sm" className="mt-3"
          onClick={() => {
            setError(null)
            setControlDraft({ legalEntityId: '', netPayAccount: '', roundingAccount: '' })
          }}
        >
          Set the control account
        </Button>
      </Card>

      <Card>
        {mappings.isPending && <Skeleton className="m-4 h-24" />}
        {mappings.isError && (
          <ErrorState error={mappings.error} onRetry={() => void mappings.refetch()} />
        )}
        {mappings.isSuccess && mappings.data.length === 0 && (
          <EmptyState
            title="Nothing is mapped"
            description="Payroll falls back to the single account code on each element, and reports the rest as unmapped."
          />
        )}
        {mappings.isSuccess && mappings.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Element</th>
                  <th className="p-3 text-start font-medium">From</th>
                  <th className="p-3 text-start font-medium">Debit</th>
                  <th className="p-3 text-start font-medium">Credit</th>
                  <th className="p-3 text-start font-medium">Cost centre</th>
                </tr>
              </thead>
              <tbody>
                {mappings.data.map((mapping) => (
                  <tr key={mapping.id} className="border-b border-slate-100">
                    <td className="p-3">
                      {mapping.elementName ?? mapping.elementCode}
                      {mapping.legalEntityId && (
                        <span className="block text-xs text-slate-500">one entity only</span>
                      )}
                    </td>
                    <td className="p-3">{mapping.effectiveFrom}</td>
                    <td className="p-3 font-mono text-xs">{mapping.debitAccount}</td>
                    <td className="p-3 font-mono text-xs">{mapping.creditAccount}</td>
                    <td className="p-3 text-slate-600">{mapping.costCentreSource}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        title="Map an element to two accounts"
        description="A journal needs a debit and a credit, and they differ by entity."
        submitLabel="Save the mapping"
        loading={saveMapping.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              await saveMapping.mutateAsync({
                elementId: draft.elementId,
                legalEntityId: draft.legalEntityId || null,
                effectiveFrom: draft.effectiveFrom,
                debitAccount: draft.debitAccount,
                creditAccount: draft.creditAccount,
                costCentreSource: draft.costCentreSource,
              })
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That mapping could not be saved')
            }
          })()
        }}
      >
        {draft && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="map-element">Element</Label>
              <Select
                id="map-element"
                className="w-full"
                value={draft.elementId}
                onChange={(event) => setDraft({ ...draft, elementId: event.target.value })}
              >
                <option value="">Choose an element…</option>
                {elements.data?.map((element) => (
                  <option key={element.id} value={element.id}>
                    {element.name} ({element.code})
                  </option>
                ))}
              </Select>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="map-debit">Debit account</Label>
                <Input
                  id="map-debit" className="font-mono" value={draft.debitAccount}
                  onChange={(event) => setDraft({ ...draft, debitAccount: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="map-credit">Credit account</Label>
                <Input
                  id="map-credit" className="font-mono" value={draft.creditAccount}
                  onChange={(event) => setDraft({ ...draft, creditAccount: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="map-entity">Entity</Label>
                <Select
                  id="map-entity"
                  className="w-full"
                  value={draft.legalEntityId}
                  onChange={(event) => setDraft({ ...draft, legalEntityId: event.target.value })}
                >
                  <option value="">Every entity in the group</option>
                  {entities.data?.map((entity) => (
                    <option key={entity.id} value={entity.id}>{entity.name}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="map-from">Effective from</Label>
                <DatePicker
                  id="map-from" value={draft.effectiveFrom}
                  onChange={(event) => setDraft({ ...draft, effectiveFrom: event.target.value })}
                />
              </div>
            </div>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={controlDraft != null}
        onOpenChange={(open) => { if (!open) setControlDraft(null) }}
        title="Net-pay control account"
        description="What the company owes its employees until payment is released."
        submitLabel="Save it"
        loading={saveControlAccount.isPending}
        error={error}
        onSubmit={() => {
          if (!controlDraft) return
          void (async () => {
            setError(null)
            try {
              await saveControlAccount.mutateAsync({
                legalEntityId: controlDraft.legalEntityId || null,
                netPayAccount: controlDraft.netPayAccount,
                roundingAccount: controlDraft.roundingAccount || null,
              })
              setControlDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That could not be saved')
            }
          })()
        }}
      >
        {controlDraft && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="control-account">Net-pay account</Label>
              <Input
                id="control-account" className="font-mono" value={controlDraft.netPayAccount}
                onChange={(event) =>
                  setControlDraft({ ...controlDraft, netPayAccount: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="control-entity">Entity</Label>
              <Select
                id="control-entity"
                className="w-full"
                value={controlDraft.legalEntityId}
                onChange={(event) =>
                  setControlDraft({ ...controlDraft, legalEntityId: event.target.value })}
              >
                <option value="">Every entity in the group</option>
                {entities.data?.map((entity) => (
                  <option key={entity.id} value={entity.id}>{entity.name}</option>
                ))}
              </Select>
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
